import {
  ApiRoutes,
  CefrLevelSchema,
  FrequencySchema,
  VocabGenerateResponseSchema,
  type Chunk,
  type CefrLevel,
  type Frequency,
  type Topic,
} from "@engo/shared";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AudioPlayer } from "../../components/ui/AudioPlayer.js";
import { Button } from "../../components/ui/button.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Modal } from "../../components/ui/Modal.js";
import { Textarea } from "../../components/ui/textarea.js";
import {
  ChunkEditor,
  chunksToDrafts,
  draftsToChunks,
  EMPTY_CHUNK_DRAFT,
  type ChunkDraft,
} from "../../components/vocab/ChunkEditor.js";
import { TopicPicker } from "../../components/vocab/TopicPicker.js";
import { ApiError, api, apiJson } from "../../lib/api.js";

function getErrorCode(err: unknown): string | undefined {
  if (err instanceof ApiError && typeof err.body === "object" && err.body !== null) {
    const body = err.body as { error?: { code?: string } };
    return body.error?.code;
  }
  return undefined;
}

type CreateFormValues = {
  word: string;
  partOfSpeech: string;
  meaningSimpleEn: string;
  vietnamese: string;
  pronunciation: string;
  examples: string;
  exampleDomain: string;
  speakingCasual: string;
  speakingFormal: string;
  speakingEmail: string;
  commonMistakes: string;
  synonyms: string;
  antonyms: string;
  memoryHook: string;
  tags: string;
  frequency: Frequency;
  cefrLevel: CefrLevel;
};

const EXAMPLE_DOMAINS = ["general", "software development", "work"] as const;

function pickRandomExampleDomain(): (typeof EXAMPLE_DOMAINS)[number] {
  return EXAMPLE_DOMAINS[Math.floor(Math.random() * EXAMPLE_DOMAINS.length)]!;
}

export function CreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [defaultExampleDomain] = useState(() => pickRandomExampleDomain());
  const { register, getValues, setValue } = useForm<CreateFormValues>({
    defaultValues: {
      word: "",
      partOfSpeech: "",
      meaningSimpleEn: "",
      vietnamese: "",
      pronunciation: "",
      examples: "",
      exampleDomain: defaultExampleDomain,
      speakingCasual: "",
      speakingFormal: "",
      speakingEmail: "",
      commonMistakes: "",
      synonyms: "",
      antonyms: "",
      memoryHook: "",
      tags: "vocab, speaking",
      frequency: "common",
      cefrLevel: "B1",
    },
  });
  const [topics, setTopics] = useState<Topic[]>(["other"]);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeTone, setNoticeTone] = useState<"neutral" | "error">("neutral");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingAudio, setIsFetchingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [chunks, setChunks] = useState<ChunkDraft[]>([
    { ...EMPTY_CHUNK_DRAFT },
    { ...EMPTY_CHUNK_DRAFT },
    { ...EMPTY_CHUNK_DRAFT },
  ]);

  async function saveEntry() {
    const values = getValues();
    const cleanWord = values.word.trim();
    const cleanMeaning = values.meaningSimpleEn.trim();
    const parsedTags = toList(values.tags);
    const parsedExamples = toLines(values.examples).slice(0, 3);
    const validChunks = draftsToChunks(chunks);

    if (!cleanWord || !cleanMeaning) {
      setNoticeTone("error");
      setNotice(t("create.validationWordMeaningRequired"));
      return;
    }
    if (validChunks.length < 3) {
      setNoticeTone("error");
      setNotice(t("create.validationChunksMin3"));
      return;
    }
    if (parsedExamples.length < 2) {
      setNoticeTone("error");
      setNotice(t("create.validationExamplesRequired"));
      return;
    }

    setIsSaving(true);
    setNotice(null);
    try {
      const data = await apiJson<unknown>(ApiRoutes.vocab, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          word: cleanWord,
          vietnamese: values.vietnamese.trim() || null,
          source_url: null,
          tags: parsedTags,
          data: {
            schema_version: 1,
            ipa: values.pronunciation.trim() || `/${cleanWord.toLowerCase()}/`,
            type: values.partOfSpeech.trim() || "word",
            meaning_simple_en: cleanMeaning,
            core_idea: cleanMeaning,
            examples: parsedExamples,
            chunks: validChunks.slice(0, 5),
            speaking_usage: {
              casual: values.speakingCasual.trim() || "Use in casual conversation.",
              neutral: values.speakingEmail.trim() || "Use in normal conversation.",
              formal: values.speakingFormal.trim() || "Use in formal communication.",
            },
            synonyms: toList(values.synonyms),
            antonyms: toList(values.antonyms),
            common_mistakes: toList(values.commonMistakes).slice(0, 4),
            memory_hook:
              values.memoryHook.trim() || `Remember "${cleanWord}" with your own sentence.`,
            vn_phrase_translation: values.vietnamese.trim() || cleanMeaning,
            topics: topics.length > 0 ? topics : ["other"],
            frequency: values.frequency,
            cefr_level: values.cefrLevel,
            ai_model: "manual",
            ai_prompt_version: "manual-v1",
            generated_at: new Date().toISOString(),
            ...(audioUrl ? { audio_url: audioUrl } : {}),
          },
        }),
      });
      const saved = data as {
        targets?: Array<{ kind?: string; status?: string; last_error?: string | null }>;
      };
      const targetSummary = (saved.targets ?? [])
        .map((target) => `${target.kind}: ${target.status}${target.last_error ? ` (${target.last_error})` : ""}`)
        .join(" | ");
      setNoticeTone("neutral");
      setNotice(
        targetSummary
          ? t("create.noticeSavedSyncStatus", { summary: targetSummary })
          : t("create.noticeSavedSuccessfully"),
      );
      onCreated();
    } catch (err) {
      setNoticeTone("error");
      if (getErrorCode(err) === "conflict") {
        setNotice(t("create.errorWordExists"));
      } else {
        setNotice(err instanceof ApiError ? err.message : t("create.errorSaveFailedGeneric"));
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function generateDraftWithAi() {
    const values = getValues();
    const cleanWord = values.word.trim();
    if (!cleanWord) {
      setNoticeTone("error");
      setNotice(t("create.validationEnterWordBeforeGenerate"));
      return;
    }

    setIsGenerating(true);
    setNotice(null);
    try {
      const data = await apiJson<unknown>(ApiRoutes.vocabGenerate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          word: cleanWord,
          vietnamese: values.vietnamese.trim() || undefined,
          example_domain: values.exampleDomain.trim() || undefined,
        }),
      });
      const parsed = VocabGenerateResponseSchema.parse(data);
      if (!parsed.generated) {
        setNoticeTone("error");
        setNotice(parsed.suggestion ?? t("create.validationAiNoPayload"));
        return;
      }
      applyGenerated(parsed.generated);
      setNoticeTone("neutral");
      setNotice(
        parsed.existing_entry_id
          ? t("create.noticeExistingEntryLoaded")
          : parsed.cached
            ? t("create.noticeAiCachedLoaded")
            : t("create.noticeAiDraftGenerated"),
      );
      api
        .fetchVocabAudio(cleanWord)
        .then((result) => {
          if (result.ipa) setValue("pronunciation", result.ipa);
          setAudioUrl(result.audio_url);
        })
        .catch(() => {
          /* ignore */
        });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t("create.errorAiGenerateFailed");
      setNoticeTone("error");
      setNotice(message);
    } finally {
      setIsGenerating(false);
    }
  }

  function applyGenerated(generated: {
    type: string;
    ipa: string;
    meaning_simple_en: string;
    vietnamese?: string;
    examples: string[];
    chunks: Chunk[];
    speaking_usage: { casual: string; formal: string; neutral: string };
    common_mistakes: string[];
    synonyms?: string[];
    antonyms?: string[];
    memory_hook: string;
    topics: Topic[];
    frequency: Frequency;
    cefr_level: CefrLevel;
  }) {
    setValue("partOfSpeech", generated.type);
    setValue("meaningSimpleEn", generated.meaning_simple_en);
    setValue("pronunciation", generated.ipa);
    if (generated.vietnamese) setValue("vietnamese", generated.vietnamese);
    setChunks(chunksToDrafts(generated.chunks));
    setValue("examples", generated.examples.join("\n"));
    setValue("speakingCasual", generated.speaking_usage.casual);
    setValue("speakingFormal", generated.speaking_usage.formal);
    setValue("speakingEmail", generated.speaking_usage.neutral);
    setValue("commonMistakes", generated.common_mistakes.join("\n"));
    setValue("synonyms", (generated.synonyms ?? []).join("\n"));
    setValue("antonyms", (generated.antonyms ?? []).join("\n"));
    setValue("memoryHook", generated.memory_hook);
    setTopics(generated.topics);
    setValue("frequency", generated.frequency);
    setValue("cefrLevel", generated.cefr_level);
  }

  async function fetchWordAudio() {
    const values = getValues();
    const word = values.word.trim();
    if (!word) {
      setNoticeTone("error");
      setNotice(t("create.validationEnterWordBeforeAudio"));
      return;
    }
    setIsFetchingAudio(true);
    setNotice(null);
    try {
      const result = await api.fetchVocabAudio(word);
      if (result.ipa) {
        setValue("pronunciation", result.ipa);
      }
      setAudioUrl(result.audio_url);
      if (!result.audio_url && !result.ipa) {
        setNoticeTone("error");
        setNotice(t("create.noticeNoAudioFound"));
      } else {
        setNoticeTone("neutral");
        setNotice(t("create.noticeAudioFetched"));
      }
    } catch (err) {
      setNoticeTone("error");
      setNotice(err instanceof ApiError ? err.message : t("create.errorAudioFetchFailed"));
    } finally {
      setIsFetchingAudio(false);
    }
  }

  return (
    <Modal
      title={t("create.title")}
      onClose={onClose}
      maxWidth="max-w-3xl"
      footer={
        <>
          <Button
            onClick={() => void generateDraftWithAi()}
            disabled={isGenerating}
            variant="gradient-outline"
          >
            {isGenerating ? t("create.generating") : t("create.aiGenerate")}
          </Button>
          <Button onClick={() => void saveEntry()} disabled={isSaving} variant="primary">
            {isSaving ? t("create.saving") : t("create.save")}
          </Button>
        </>
      }
    >
      {notice ? (
        <p className={`text-xs ${noticeTone === "error" ? "text-rose-300" : "text-[var(--text-muted)]"}`}>
          {notice}
        </p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
          <Field label={t("create.fieldWord")}>
            <Input {...register("word")} placeholder={t("create.placeholderWord")} />
          </Field>
          <Field label={t("create.fieldPartOfSpeech")}>
            <Input {...register("partOfSpeech")} placeholder={t("create.placeholderPartOfSpeech")} />
          </Field>
          <Field label={t("create.fieldPronunciation")}>
            <div className="flex items-center gap-2">
              <Input
                className="min-w-0 flex-1"
                {...register("pronunciation")}
                placeholder={t("create.placeholderPronunciation")}
              />
              {audioUrl ? (
                <AudioPlayer
                  src={audioUrl}
                  onRefetch={() => void fetchWordAudio()}
                  isRefetching={isFetchingAudio}
                  className="flex-1 min-w-0"
                />
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void fetchWordAudio()}
                  disabled={isFetchingAudio}
                >
                  {isFetchingAudio ? t("create.fetchingAudio") : t("create.fetchAudio")}
                </Button>
              )}
            </div>
          </Field>
          <Field label={t("create.fieldExampleDomain")}>
            <select
              {...register("exampleDomain")}
              className="w-full rounded-lg border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] outline-none ring-0 transition-colors focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/25"
            >
              <option value="general" className="bg-[#0f172a] text-white">
                {t("create.optionGeneral")}
              </option>
              <option value="software development" className="bg-[#0f172a] text-white">
                {t("create.optionSoftwareDevelopment")}
              </option>
              <option value="work" className="bg-[#0f172a] text-white">
                {t("create.optionWork")}
              </option>
            </select>
          </Field>
          <Field label={t("create.fieldFrequency")}>
            <select
              {...register("frequency")}
              className="w-full rounded-lg border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] outline-none ring-0 transition-colors focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/25"
            >
              {FrequencySchema.options.map((option) => (
                <option key={option} value={option} className="bg-[#0f172a] text-white">
                  {t(`frequency.${option}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("create.fieldCefrLevel")}>
            <select
              {...register("cefrLevel")}
              className="w-full rounded-lg border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] outline-none ring-0 transition-colors focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/25"
            >
              {CefrLevelSchema.options.map((option) => (
                <option key={option} value={option} className="bg-[#0f172a] text-white">
                  {option}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div>
          <Label>{t("create.fieldTopics")}</Label>
          <div className="mt-1">
            <TopicPicker value={topics} onChange={setTopics} />
          </div>
        </div>

        <Field label={t("create.fieldMeaning")}>
          <Textarea
            {...register("meaningSimpleEn")}
            rows={2}
            placeholder={t("create.placeholderMeaning")}
          />
        </Field>

        <Field label={t("create.fieldVietnameseMeaning")}>
          <Textarea
            {...register("vietnamese")}
            rows={2}
            placeholder={t("create.placeholderVietnamese")}
          />
        </Field>

        <div>
          <Label>{t("create.fieldChunks")}</Label>
          <div className="mt-1">
            <ChunkEditor value={chunks} onChange={setChunks} />
          </div>
        </div>

        <Field label={t("create.fieldExamples")}>
          <Textarea
            {...register("examples")}
            rows={4}
            placeholder={t("create.placeholderExamples")}
          />
        </Field>

        <div className="grid gap-3 md:grid-cols-3">
          <Field label={t("create.fieldSpeakingCasual")}>
            <Textarea {...register("speakingCasual")} rows={2} />
          </Field>
          <Field label={t("create.fieldSpeakingFormal")}>
            <Textarea {...register("speakingFormal")} rows={2} />
          </Field>
          <Field label={t("create.fieldSpeakingEmail")}>
            <Textarea {...register("speakingEmail")} rows={2} />
          </Field>
        </div>

        <Field label={t("create.fieldCommonMistakes")}>
          <Textarea {...register("commonMistakes")} rows={3} />
        </Field>

        <Field label={t("create.fieldSynonyms")}>
          <Textarea {...register("synonyms")} rows={3} />
        </Field>

        <Field label={t("create.fieldAntonyms")}>
          <Textarea {...register("antonyms")} rows={3} />
        </Field>

        <Field label={t("create.fieldMemoryHook")}>
          <Textarea {...register("memoryHook")} rows={3} />
        </Field>

        <Field label={t("create.fieldTags")}>
          <Input {...register("tags")} />
        </Field>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      {children}
    </label>
  );
}

function toList(raw: string): string[] {
  return raw
    .split(/\r?\n|,/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function toLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);
}

